import { InternalId, IPagedListResponse, RuntimeDate } from '@edgebox/data-definition-kit';
import { SyncCoreDataDefinitionsEnumTranslator, SyndicationStatus, SyndicationType, TaskStatus } from '@edgebox/sync-core-data-definitions';
import { ClientTaskEntity, ClientTaskGroupEntityWithSummary } from '@edgebox/sync-core-rest-client';
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useBrandPresenceRouteSync } from './useGovernanceRouteSync';
import { SyndicationStatusIcon } from '../SyndicationStatusIcon';
import { SyncCoreApiContext } from '../../../contexts/SyncCoreApiContext';
import { GovernanceJobRow } from './Components/GovernanceJobRow';
import {
  collapsedOperationErrors,
  countOperationsNeedingAttention,
  isOperationWaiting,
  latestOperationError,
  operationErrorTone,
  sortedOperationErrors,
  waitingStatusLabel,
} from './Components/governanceJobWaiting.utils';
import { GovernanceJobsViewMode, GovernanceJobsViewSwitch } from './Components/GovernanceJobsViewSwitch';
import { faArrowLeft, faListCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BrandPresenceEyebrow, BrandPresencePageHeader } from './Components/BrandPresencePageHeader';
import { BrandPresenceEmptyState } from './Components/BrandPresenceEmptyState';
import { BrandPresencePagination } from './Components/BrandPresencePagination';
import { JobDetailSkeleton, JobListSkeleton } from './Components/GovernanceJobsSkeleton';
import { BP_CARD_CLASSES, BP_TOOLBAR_BUTTON_CLASSES } from './Components/brandPresenceClasses';
import { formatDate } from './Components/brandPresenceDates';
import { TaskDomain } from '@edgebox/sync-core-data-definitions';

/** A detail figure's label: the typewriter face, small, above its value. */
const DETAIL_LABEL_SHAPE = 'mb-1 font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em]';
const DETAIL_LABEL_CLASSES = `${DETAIL_LABEL_SHAPE} text-slate-500`;

interface IProps {
  routeMode?: GovernanceJobsViewMode;
  detailId?: string;
  onNavigate: (path: string) => void;
}

function toTitle(value: string | undefined) {
  if (!value) {
    return '-';
  }

  return value
    .replace(/^.*\./, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function translateEnumValue(
  enumName: 'MigrationType' | 'SyndicationType' | 'SyndicationOperationType' | 'SyndicationOperationSubType',
  value?: string
) {
  if (!value) {
    return '-';
  }

  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue(enumName, value as any);
  } catch {
    return toTitle(value);
  }
}

function taskGroupTypeLabel(type?: string) {
  return translateEnumValue('MigrationType', type);
}

function taskTypeLabel(type?: string) {
  return translateEnumValue('SyndicationType', type);
}

function crawlTaskUrl(task?: { type?: string; url?: string }) {
  if (task?.type === SyndicationType.CrawlUrl && task.url) {
    return task.url;
  }

  return undefined;
}

function operationTypeLabel(type?: string) {
  return translateEnumValue('SyndicationOperationType', type);
}

function operationSubTypeLabel(subType?: string) {
  if (!subType) {
    return undefined;
  }

  return translateEnumValue('SyndicationOperationSubType', subType);
}

function formatDurationMs(durationMs?: number, createdAt?: RuntimeDate, finishedAt?: RuntimeDate, processing?: boolean) {
  if (processing) {
    return 'Running...';
  }

  let value = durationMs;
  if ((!value || value <= 0) && createdAt && finishedAt) {
    value = Math.max(0, finishedAt.valueOf() - createdAt.valueOf());
  }

  if (!value || value <= 0) {
    return '-';
  }

  const totalSeconds = Math.round(value / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function isRetrying(status?: string) {
  return !!status?.includes(TaskStatus.Retrying);
}

function isFailed(status?: string) {
  return !!status?.includes(TaskStatus.Failed) || !!status?.includes(TaskStatus.Aborted) || !!status?.includes(TaskStatus.LimitExceeded);
}

function isProcessing(status?: string) {
  return !!status?.includes(TaskStatus.Initializing) || !!status?.includes(TaskStatus.Running) || !!status?.includes(TaskStatus.Retrying);
}

function formatTimestamp(value?: number) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${month} ${day}, ${hours}:${minutes}`;
}

function formatErrorTimestamp(value?: number) {
  if (!value) {
    return '-';
  }

  return formatTimestamp(value);
}

function operationSubTitle(operation: { subType?: string; language?: string; extractionType?: string; actionId?: string }) {
  const details = [operationSubTypeLabel(operation.subType), operation.language, operation.extractionType, operation.actionId].filter(
    Boolean
  );
  if (!details.length) {
    return undefined;
  }

  return details.map((value) => toTitle(value)).join(' · ');
}

function statusLabel(status?: string) {
  if (!status) {
    return '-';
  }

  return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('SyndicationStatus', status as TaskStatus);
}

type TaskDetailActionType = 'restart' | 'abort';

function taskDetailAction(status?: string): { label: 'Rerun' | 'Retry' | 'Abort'; action: TaskDetailActionType } {
  if (!!status?.includes(TaskStatus.Finished)) {
    return { label: 'Rerun', action: 'restart' };
  }

  if (isProcessing(status)) {
    return { label: 'Abort', action: 'abort' };
  }

  return { label: 'Retry', action: 'restart' };
}

function taskGroupDetailAction(status?: string): { label: 'Abort'; action: 'abort' } | undefined {
  if (isProcessing(status)) {
    return { label: 'Abort', action: 'abort' };
  }

  return undefined;
}

export function GovernanceJobsScreen({ routeMode, detailId, onNavigate }: IProps) {
  const apiContext = React.useContext(SyncCoreApiContext);
  const api = apiContext?.api;

  const [page, setPage] = React.useState(0);
  const [itemsPerPage] = React.useState(20);
  const [loading, setLoading] = React.useState(true);
  const [taskActionLoading, setTaskActionLoading] = React.useState(false);
  const [taskGroupResult, setTaskGroupResult] = React.useState<IPagedListResponse<ClientTaskGroupEntityWithSummary> | undefined>(undefined);
  const [taskResult, setTaskResult] = React.useState<IPagedListResponse<ClientTaskEntity> | undefined>(undefined);
  const [taskGroupDetail, setTaskGroupDetail] = React.useState<ClientTaskGroupEntityWithSummary | undefined>(undefined);
  const [taskGroupTasks, setTaskGroupTasks] = React.useState<IPagedListResponse<ClientTaskEntity> | undefined>(undefined);
  const [taskDetail, setTaskDetail] = React.useState<ClientTaskEntity | undefined>(undefined);
  const [expandedOperationErrors, setExpandedOperationErrors] = React.useState<Record<string, boolean>>({});

  const refreshRequestIdRef = React.useRef(0);
  const detailPollingIntervalRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const detailPollingRequestInFlightRef = React.useRef(false);

  const mode: GovernanceJobsViewMode = routeMode || 'task-group';
  const isDetail = !!detailId;

  const changed = React.useCallback((nextValue: unknown, currentValue: unknown) => {
    return JSON.stringify(nextValue) !== JSON.stringify(currentValue);
  }, []);

  const refreshDetailSilently = React.useCallback(async () => {
    if (!api || detailPollingRequestInFlightRef.current || !isDetail || !detailId) {
      return;
    }

    const detailInternalId = detailId as InternalId;
    detailPollingRequestInFlightRef.current = true;
    try {
      if (mode === 'task-group') {
        const [nextTaskGroupDetail, nextTaskGroupTasks] = await Promise.all([
          api.service.taskGroups.itemWithSummary(detailInternalId, true),
          api.service.tasks.list({ domains: [TaskDomain.Governance], migrationId: detailInternalId }, { page: 0, itemsPerPage: 200 }),
        ]);

        const detailChanged = changed(nextTaskGroupDetail, taskGroupDetail);
        const tasksChanged = changed(nextTaskGroupTasks, taskGroupTasks);

        if (detailChanged || tasksChanged) {
          setTaskGroupDetail(nextTaskGroupDetail);
          setTaskGroupTasks(nextTaskGroupTasks);
          setTaskDetail(undefined);
        }

        return;
      }

      if (mode === 'task') {
        const nextTaskDetail = await api.service.tasks.item(detailInternalId, true, { includeUsage: true });
        if (changed(nextTaskDetail, taskDetail)) {
          setTaskDetail(nextTaskDetail);
          setTaskGroupDetail(undefined);
          setTaskGroupTasks(undefined);
        }
      }
    } finally {
      detailPollingRequestInFlightRef.current = false;
    }
  }, [api, changed, detailId, isDetail, mode, taskDetail, taskGroupDetail, taskGroupTasks]);

  const refresh = React.useCallback(async () => {
    if (!api) {
      setLoading(false);
      return;
    }

    const requestId = ++refreshRequestIdRef.current;
    setLoading(true);

    if (detailId) {
      const detailInternalId = detailId as InternalId;

      if (mode === 'task-group') {
        const [nextTaskGroupDetail, nextTaskGroupTasks] = await Promise.all([
          api.service.taskGroups.itemWithSummary(detailInternalId, true),
          api.service.tasks.list({ domains: [TaskDomain.Governance], migrationId: detailInternalId }, { page: 0, itemsPerPage: 200 }),
        ]);
        if (requestId !== refreshRequestIdRef.current) {
          return;
        }

        setTaskGroupDetail(nextTaskGroupDetail);
        setTaskGroupTasks(nextTaskGroupTasks);
        setTaskDetail(undefined);
        setTaskGroupResult(undefined);
        setTaskResult(undefined);
        setLoading(false);
        return;
      }

      const nextTaskDetail = await api.service.tasks.item(detailInternalId, true, { includeUsage: true });
      if (requestId !== refreshRequestIdRef.current) {
        return;
      }

      setTaskDetail(nextTaskDetail);
      setTaskGroupDetail(undefined);
      setTaskGroupTasks(undefined);
      setTaskGroupResult(undefined);
      setTaskResult(undefined);
      setLoading(false);
      return;
    }

    if (mode === 'task-group') {
      const nextTaskGroupResult = await api.service.taskGroups.listWithSummary(
        { domains: [TaskDomain.Governance] },
        { page, itemsPerPage }
      );
      if (requestId !== refreshRequestIdRef.current) {
        return;
      }

      setTaskGroupResult(nextTaskGroupResult);
      setTaskResult(undefined);
      setTaskGroupDetail(undefined);
      setTaskGroupTasks(undefined);
      setTaskDetail(undefined);
      setLoading(false);
      return;
    }

    const nextTaskResult = await api.service.tasks.list({ domains: [TaskDomain.Governance] }, { page, itemsPerPage });
    if (requestId !== refreshRequestIdRef.current) {
      return;
    }

    setTaskResult(nextTaskResult);
    setTaskGroupResult(undefined);
    setTaskGroupDetail(undefined);
    setTaskGroupTasks(undefined);
    setTaskDetail(undefined);
    setLoading(false);
  }, [api, detailId, itemsPerPage, mode, page]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const shouldPollDetail = React.useMemo(() => {
    if (!isDetail) {
      return false;
    }

    if (mode === 'task') {
      return isProcessing(taskDetail?.status);
    }

    if (mode === 'task-group') {
      return isProcessing(taskGroupDetail?.status);
    }

    return false;
  }, [isDetail, mode, taskDetail?.status, taskGroupDetail?.status]);

  React.useEffect(() => {
    if (shouldPollDetail) {
      if (!detailPollingIntervalRef.current) {
        detailPollingIntervalRef.current = setInterval(() => {
          void refreshDetailSilently();
        }, 5000);
      }
      return () => {
        if (detailPollingIntervalRef.current) {
          clearInterval(detailPollingIntervalRef.current);
          detailPollingIntervalRef.current = undefined;
        }
      };
    }

    if (detailPollingIntervalRef.current) {
      clearInterval(detailPollingIntervalRef.current);
      detailPollingIntervalRef.current = undefined;
    }

    return () => {
      if (detailPollingIntervalRef.current) {
        clearInterval(detailPollingIntervalRef.current);
        detailPollingIntervalRef.current = undefined;
      }
    };
  }, [refreshDetailSilently, shouldPollDetail]);

  const executeTaskDetailAction = React.useCallback(async () => {
    if (!api || !taskDetail) {
      return;
    }

    const detailInternalId = taskDetail.id as InternalId;
    const action = taskDetailAction(taskDetail.status);

    setTaskActionLoading(true);
    try {
      if (action.action === 'abort') {
        await api.service.tasks.abort([detailInternalId]);
      } else {
        await api.service.tasks.restart(detailInternalId);
      }

      await refresh();
    } finally {
      setTaskActionLoading(false);
    }
  }, [api, refresh, taskDetail]);

  const executeTaskGroupDetailAction = React.useCallback(async () => {
    if (!api || !taskGroupDetail) {
      return;
    }

    const detailInternalId = taskGroupDetail.id as InternalId;

    setTaskActionLoading(true);
    try {
      await api.service.taskGroups.abort(detailInternalId);
      await refresh();
    } finally {
      setTaskActionLoading(false);
    }
  }, [api, refresh, taskGroupDetail]);

  const renderDetail = () => {
    if (mode === 'task-group' && taskGroupDetail) {
      const warningCount = (taskGroupDetail.summary || [])
        .filter((status) => isRetrying(status.status))
        .reduce((sum, status) => sum + status.count, 0);
      const errorCount = (taskGroupDetail.summary || [])
        .filter((status) => isFailed(status.status))
        .reduce((sum, status) => sum + status.count, 0);
      const taskCount = (taskGroupDetail.summary || []).reduce((sum, status) => sum + status.count, 0);

      return (
        <div className="space-y-6">
          <div className={`${BP_CARD_CLASSES} p-6 motion-safe:animate-bp-fade-up`}>
            <BrandPresenceEyebrow>Task group details</BrandPresenceEyebrow>
            <h2 className="m-0 mb-6 text-[1.5rem] font-bold tracking-tight text-slate-900">{taskGroupTypeLabel(taskGroupDetail.type)}</h2>

            <div className="grid grid-cols-1 gap-4 rounded-xl bg-slate-50/80 p-4 md:grid-cols-4">
              <div>
                <p className={DETAIL_LABEL_CLASSES}>ID</p>
                <p className="m-0 break-all font-eyebrow text-xs text-slate-700">{taskGroupDetail.id}</p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Started</p>
                <p className="text-sm text-slate-700">{formatDate(taskGroupDetail.createdAt)}</p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Duration</p>
                <p className="text-sm text-slate-700">
                  {formatDurationMs(undefined, taskGroupDetail.createdAt, taskGroupDetail.updatedAt, isProcessing(taskGroupDetail.status))}
                </p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Status</p>
                <div className="flex items-center gap-2">
                  <SyndicationStatusIcon status={taskGroupDetail.status as SyndicationStatus} size="sm" />
                  <p className="text-sm text-slate-700">{statusLabel(taskGroupDetail.status)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
                <p className={DETAIL_LABEL_CLASSES}>Tasks</p>
                <p className="m-0 text-[1.75rem] leading-none font-bold tracking-tight text-slate-900 tabular-nums">{taskCount}</p>
              </div>
              <div className={`rounded-xl p-4 ring-1 ring-inset ${warningCount > 0 ? 'bg-amber-50 ring-amber-200' : 'ring-slate-200/80'}`}>
                <p className={`${DETAIL_LABEL_SHAPE} ${warningCount > 0 ? 'text-amber-700' : 'text-slate-500'}`}>Warnings</p>
                <p
                  className={`m-0 text-[1.75rem] leading-none font-bold tracking-tight tabular-nums ${warningCount > 0 ? 'text-amber-700' : 'text-slate-900'}`}
                >
                  {warningCount}
                </p>
              </div>
              <div className={`rounded-xl p-4 ring-1 ring-inset ${errorCount > 0 ? 'bg-rose-50 ring-rose-200' : 'ring-slate-200/80'}`}>
                <p className={`${DETAIL_LABEL_SHAPE} ${errorCount > 0 ? 'text-rose-700' : 'text-slate-500'}`}>Errors</p>
                <p
                  className={`m-0 text-[1.75rem] leading-none font-bold tracking-tight tabular-nums ${errorCount > 0 ? 'text-rose-700' : 'text-slate-900'}`}
                >
                  {errorCount}
                </p>
              </div>
            </div>
          </div>

          <h3 className="m-0 text-base font-bold text-slate-900">Tasks</h3>
          {!taskGroupTasks?.items.length ? (
            <div className={`${BP_CARD_CLASSES} px-6 py-10 text-center text-sm text-slate-500`}>No tasks found for this task group.</div>
          ) : (
            <div className="space-y-3">
              {taskGroupTasks.items.map((item) => {
                const itemWarningCount = countOperationsNeedingAttention(item.operations || []);
                const itemErrorCount = (item.operations || []).filter((operation) => isFailed(operation.status)).length;
                const operationCount = item.operations?.length || 0;
                const processing = isProcessing(item.status);

                return (
                  <GovernanceJobRow
                    key={item.id}
                    processing={processing}
                    title={taskTypeLabel(item.type)}
                    titleTooltip={crawlTaskUrl(item)}
                    subtitle={`${operationCount} operations · ${statusLabel(item.status)}`}
                    startedAt={formatDate(item.createdAt)}
                    duration={formatDurationMs(item.duration, item.createdAt, item.finishedAt, processing)}
                    warningCount={itemWarningCount}
                    errorCount={itemErrorCount}
                    leftBorder={itemErrorCount > 0 ? 'error' : itemWarningCount > 0 ? 'warning' : 'none'}
                    onClick={() => onNavigate(`/brand-presence/jobs/tasks/${item.id}`)}
                  />
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (mode === 'task' && taskDetail) {
      const warningCount = countOperationsNeedingAttention(taskDetail.operations || []);
      const errorCount = (taskDetail.operations || []).filter((operation) => isFailed(operation.status)).length;
      const operationCount = taskDetail.operations?.length || 0;
      const operations = taskDetail.operations || [];

      return (
        <div className="space-y-6">
          <div className={`${BP_CARD_CLASSES} p-6 motion-safe:animate-bp-fade-up`}>
            <BrandPresenceEyebrow>Task details</BrandPresenceEyebrow>
            <h2 className="m-0 text-[1.5rem] font-bold tracking-tight text-slate-900">{taskTypeLabel(taskDetail.type)}</h2>
            {crawlTaskUrl(taskDetail) ? (
              <p className="mt-1 mb-0 break-all font-eyebrow text-xs text-slate-400">{crawlTaskUrl(taskDetail)}</p>
            ) : null}
            <div className="mb-6" />

            <div className="grid grid-cols-1 gap-4 rounded-xl bg-slate-50/80 p-4 md:grid-cols-4">
              <div>
                <p className={DETAIL_LABEL_CLASSES}>ID</p>
                <p className="m-0 break-all font-eyebrow text-xs text-slate-700">{taskDetail.id}</p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Started</p>
                <p className="text-sm text-slate-700">{formatDate(taskDetail.createdAt)}</p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Duration</p>
                <p className="text-sm text-slate-700">
                  {formatDurationMs(taskDetail.duration, taskDetail.createdAt, taskDetail.finishedAt, isProcessing(taskDetail.status))}
                </p>
              </div>
              <div>
                <p className={DETAIL_LABEL_CLASSES}>Status</p>
                <div className="flex items-center gap-2">
                  <SyndicationStatusIcon status={taskDetail.status as SyndicationStatus} size="sm" />
                  <p className="text-sm text-slate-700">{statusLabel(taskDetail.status)}</p>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4">
              <div className="rounded-xl p-4 ring-1 ring-inset ring-slate-200/80">
                <p className={DETAIL_LABEL_CLASSES}>Operations</p>
                <p className="m-0 text-[1.75rem] leading-none font-bold tracking-tight text-slate-900 tabular-nums">{operationCount}</p>
              </div>
              <div className={`rounded-xl p-4 ring-1 ring-inset ${warningCount > 0 ? 'bg-amber-50 ring-amber-200' : 'ring-slate-200/80'}`}>
                <p className={`${DETAIL_LABEL_SHAPE} ${warningCount > 0 ? 'text-amber-700' : 'text-slate-500'}`}>Warnings</p>
                <p
                  className={`m-0 text-[1.75rem] leading-none font-bold tracking-tight tabular-nums ${warningCount > 0 ? 'text-amber-700' : 'text-slate-900'}`}
                >
                  {warningCount}
                </p>
              </div>
              <div className={`rounded-xl p-4 ring-1 ring-inset ${errorCount > 0 ? 'bg-rose-50 ring-rose-200' : 'ring-slate-200/80'}`}>
                <p className={`${DETAIL_LABEL_SHAPE} ${errorCount > 0 ? 'text-rose-700' : 'text-slate-500'}`}>Errors</p>
                <p
                  className={`m-0 text-[1.75rem] leading-none font-bold tracking-tight tabular-nums ${errorCount > 0 ? 'text-rose-700' : 'text-slate-900'}`}
                >
                  {errorCount}
                </p>
              </div>
            </div>
          </div>

          <h3 className="m-0 text-base font-bold text-slate-900">Action Log</h3>
          <div className={`${BP_CARD_CLASSES} overflow-hidden`}>
            <div className="grid grid-cols-12 gap-4 border-b border-slate-200/70 bg-slate-50/70 px-6 py-3 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-slate-500">
              <div className="col-span-4">Type</div>
              <div className="col-span-3">Completed at</div>
              <div className="col-span-5">Status</div>
            </div>
            {!operations.length ? (
              <div className="px-6 py-10 text-center text-sm text-slate-500">No operations found.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {operations.map((operation, index) => {
                  const subtitle = operationSubTitle(operation);
                  const errors = sortedOperationErrors(operation);
                  const failed = isFailed(operation.status);
                  const waiting = isOperationWaiting(operation);
                  // A waiting operation is still `Retrying`, so the amber "needs attention" tint
                  // only applies to a retry that is not a deliberate wait.
                  const retrying = isRetrying(operation.status) && !waiting;
                  const running = isProcessing(operation.status);
                  const operationKey = `${operation.type}-${index}`;
                  const expanded = !!expandedOperationErrors?.[operationKey];
                  const visibleErrors = expanded ? errors : collapsedOperationErrors(errors);
                  const hiddenErrorCount = Math.max(0, errors.length - visibleErrors.length);

                  const endText = operation.endTime ? formatDate(operation.endTime) : running ? 'Running...' : '-';

                  const rowClass = failed ? 'bg-rose-50/40' : retrying ? 'bg-amber-50/40' : 'bg-white';

                  return (
                    <div key={`${operation.type}-${index}`} className={`grid grid-cols-12 gap-4 px-6 py-4 items-start ${rowClass}`}>
                      <div className="col-span-4 min-w-0">
                        <span className="inline-flex items-center rounded-full bg-primary/[0.08] px-2.5 py-0.5 text-xs font-semibold text-sky-800 ring-1 ring-inset ring-primary/20">
                          {operationTypeLabel(operation.type)}
                        </span>
                        {subtitle ? <p className="mt-1 text-xs text-slate-500">{subtitle}</p> : null}
                      </div>
                      <div className="col-span-3">
                        <p className="text-sm text-slate-600">
                          <span className={running ? 'font-semibold text-primary' : 'text-slate-600 tabular-nums'}>{endText}</span>
                        </p>
                      </div>
                      <div className="col-span-5">
                        <div className="flex items-center gap-2">
                          <SyndicationStatusIcon
                            status={operation.status as SyndicationStatus}
                            errorType={latestOperationError(operation)?.type}
                            size="sm"
                          />
                          <p className="m-0 text-sm font-semibold text-slate-800">
                            {waiting ? waitingStatusLabel() : statusLabel(operation.status)}
                          </p>
                        </div>
                        {visibleErrors.map((error, errorIndex) => (
                          <div
                            key={`${operationKey}-error-${error.timestamp}-${errorIndex}`}
                            // Each entry is toned by its OWN type, so a genuine failure still reads
                            // as one even when the newest entry above it is a wait.
                            className={`mt-2 rounded-lg border bg-white px-3 py-2 text-sm ${
                              operationErrorTone(error) === 'waiting'
                                ? 'border-slate-200 text-slate-500'
                                : failed || retrying
                                  ? 'border-rose-200 text-rose-600'
                                  : 'border-slate-200 text-slate-500'
                            }`}
                          >
                            <span className="font-semibold tabular-nums">{formatErrorTimestamp(error.timestamp)}</span>
                            <span className="mx-2 text-slate-300">·</span>
                            <span>{error.errorMessage || '-'}</span>
                          </div>
                        ))}
                        {hiddenErrorCount > 0 ? (
                          <button
                            type="button"
                            className="mt-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/[0.07]"
                            onClick={() =>
                              setExpandedOperationErrors((prevState) => ({
                                ...(prevState || {}),
                                [operationKey]: true,
                              }))
                            }
                          >
                            + {hiddenErrorCount}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className={BP_CARD_CLASSES}>
        <BrandPresenceEmptyState
          icon={faTriangleExclamation}
          title="Details not found."
          description="This job may no longer be available. Pick it again from the list."
        />
      </div>
    );
  };

  const result = mode === 'task-group' ? taskGroupResult : taskResult;

  // Jobs and tasks share this screen, so a page number can outlive a switch between them; it is
  // brought back into range once the list says how many pages there are.
  const lastPage = result ? Math.max(0, result.numberOfPages - 1) : undefined;
  React.useEffect(() => {
    if (lastPage !== undefined && page > lastPage) {
      setPage(lastPage);
    }
  }, [lastPage, page]);
  const taskAction = isDetail && mode === 'task' && !!taskDetail ? taskDetailAction(taskDetail.status) : undefined;
  const taskGroupAction =
    isDetail && mode === 'task-group' && !!taskGroupDetail ? taskGroupDetailAction(taskGroupDetail.status) : undefined;

  const backToList = () => onNavigate(`/brand-presence/jobs/${mode === 'task-group' ? 'groups' : 'tasks'}`);
  const detailAction = taskAction
    ? { label: taskAction.label, run: () => void executeTaskDetailAction() }
    : taskGroupAction
      ? { label: taskGroupAction.label, run: () => void executeTaskGroupDetailAction() }
      : undefined;

  return (
    <div className="relative w-full h-full bg-slate-50 text-slate-900 bg-brand-page">
      <main className="relative mx-auto max-w-[1600px] px-6 py-8">
        {isDetail ? (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4 motion-safe:animate-bp-fade-up">
            <button
              type="button"
              className="group/back -ml-2 inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-semibold text-slate-500 transition-colors hover:bg-white/70 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              onClick={backToList}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="text-xs transition-transform duration-150 group-hover/back:-translate-x-0.5" />
              Back to jobs
            </button>
            {detailAction ? (
              <button type="button" className={BP_TOOLBAR_BUTTON_CLASSES} disabled={!!taskActionLoading} onClick={detailAction.run}>
                {taskActionLoading ? `${detailAction.label}...` : detailAction.label}
              </button>
            ) : null}
          </div>
        ) : (
          <BrandPresencePageHeader
            eyebrow={
              <>
                Brand Presence <span className="text-slate-300">/</span> Jobs
              </>
            }
            title="Jobs"
            description="The work running for you in the background: indexing pages, checking their health and preparing optimizations."
            actions={
              <GovernanceJobsViewSwitch
                mode={mode}
                onChange={(nextMode) => onNavigate(`/brand-presence/jobs/${nextMode === 'task-group' ? 'groups' : 'tasks'}`)}
              />
            }
          />
        )}

        {loading ? (
          isDetail ? (
            <JobDetailSkeleton />
          ) : (
            <JobListSkeleton />
          )
        ) : isDetail ? (
          renderDetail()
        ) : !result || !result.items.length ? (
          <div className={BP_CARD_CLASSES}>
            <BrandPresenceEmptyState
              icon={faListCheck}
              title="No jobs found."
              description="Jobs appear here as soon as pages are indexed, checked or optimized."
            />
          </div>
        ) : (
          <div className="space-y-3">
            {mode === 'task-group'
              ? taskGroupResult?.items.map((item) => {
                  const warningCount = (item.summary || [])
                    .filter((status) => isRetrying(status.status))
                    .reduce((sum, status) => sum + status.count, 0);
                  const errorCount = (item.summary || [])
                    .filter((status) => isFailed(status.status))
                    .reduce((sum, status) => sum + status.count, 0);
                  const taskCount = (item.summary || []).reduce((sum, status) => sum + status.count, 0);
                  const processing = isProcessing(item.status);

                  return (
                    <GovernanceJobRow
                      key={item.id}
                      processing={processing}
                      title={taskGroupTypeLabel(item.type)}
                      subtitle={`${taskCount} tasks`}
                      startedAt={formatDate(item.createdAt)}
                      duration={formatDurationMs(undefined, item.createdAt, item.updatedAt, processing)}
                      warningCount={warningCount}
                      errorCount={errorCount}
                      leftBorder={errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : 'none'}
                      onClick={() => onNavigate(`/brand-presence/jobs/groups/${item.id}`)}
                    />
                  );
                })
              : taskResult?.items.map((item) => {
                  const warningCount = countOperationsNeedingAttention(item.operations || []);
                  const errorCount = (item.operations || []).filter((operation) => isFailed(operation.status)).length;
                  const operationCount = item.operations?.length || 0;
                  const processing = isProcessing(item.status);

                  return (
                    <GovernanceJobRow
                      key={item.id}
                      processing={processing}
                      title={taskTypeLabel(item.type)}
                      titleTooltip={crawlTaskUrl(item)}
                      subtitle={`${operationCount} operations`}
                      startedAt={formatDate(item.createdAt)}
                      duration={formatDurationMs(item.duration, item.createdAt, item.finishedAt, processing)}
                      warningCount={warningCount}
                      errorCount={errorCount}
                      leftBorder={errorCount > 0 ? 'error' : warningCount > 0 ? 'warning' : 'none'}
                      onClick={() => onNavigate(`/brand-presence/jobs/tasks/${item.id}`)}
                    />
                  );
                })}
          </div>
        )}

        {!isDetail && result && result.items.length ? (
          <div className={`mt-6 ${BP_CARD_CLASSES}`}>
            <BrandPresencePagination
              page={result.page}
              numberOfPages={Math.max(1, result.numberOfPages)}
              itemsPerPage={result.itemsPerPage}
              totalNumberOfItems={result.totalNumberOfItems}
              itemLabel={result.totalNumberOfItems === 1 ? 'job' : 'jobs'}
              onPageChange={setPage}
            />
          </div>
        ) : null}
      </main>
    </div>
  );
}

export function GovernanceJobs() {
  const location = useLocation();
  const navigate = useNavigate();

  const parts = location.pathname.split('/').filter(Boolean);
  const view = parts[2];
  const id = parts[3];

  const routeMode: GovernanceJobsViewMode | undefined = view === 'groups' ? 'task-group' : view === 'tasks' ? 'task' : undefined;

  useBrandPresenceRouteSync({
    embedPrefix: '/brand-presence/jobs',
    appPrefix: '/brand-presence/jobs',
    defaultEmbedPath: '/brand-presence/jobs/groups',
    skipBasePathUpdate: true,
  });

  return <GovernanceJobsScreen routeMode={routeMode} detailId={id} onNavigate={(path) => navigate(path)} />;
}
